import { useState, useEffect, useMemo } from "react";
import { Instagram, ExternalLink } from "lucide-react";

const InstagramHub = () => {
  const [posts, setPosts] = useState([]);
  const [hasToken, setHasToken] = useState(false);
  const [loading, setLoading] = useState(true);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Fallback images (your current good images)
  const fallbackPosts = useMemo(() => [
    { 
      id: "1", 
      type: "image",
      image: "https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev/images/z3hijfgb-kenny-filmt-bts.png",
      link: "https://instagram.com/kennyfrey_",
      caption: "Kenny beim Filmen - McLaren BTS"
    },
    { 
      id: "2", 
      type: "image",
      image: "https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev/images/bts-mclaren-senna.webp",
      link: "https://instagram.com/kennyfrey_",
      caption: "BTS McLaren Senna Shoot"
    },
    { 
      id: "3", 
      type: "image",
      image: "https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev/images/bts-seedos-set.webp",
      link: "https://instagram.com/kennyfrey_",
      caption: "Behind the Scenes"
    },
    { 
      id: "4", 
      type: "image",
      image: "https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev/images/ga6booee-kenny-fuehrt-regie-bei-einem-projekt.png",
      link: "https://instagram.com/kennyfrey_",
      caption: "Kenny führt Regie"
    },
    { 
      id: "5", 
      type: "image",
      image: "https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev/images/ngkbrd3s-250429-vs-uhrenindustriemuseum-l1630982.jpg",
      link: "https://instagram.com/kennyfrey_",
      caption: "BTS - Team bei der Arbeit"
    },
    { 
      id: "6", 
      type: "image",
      image: "https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev/images/psuy5rxn-250429-vs-uhrenindustriemuseum-l1640155.jpg",
      link: "https://instagram.com/kennyfrey_",
      caption: "Kenny mit Cinema-Kamera am Set"
    },
  ], []);

  useEffect(() => {
    const fetchInstagramPosts = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/instagram/feed`);
        const data = await response.json();
        
        setHasToken(data.has_token);
        
        if (data.posts && data.posts.length > 0) {
          setPosts(data.posts);
        } else {
          // Use fallback images if no posts
          setPosts(fallbackPosts);
        }
      } catch (error) {
        console.error("Error fetching Instagram posts:", error);
        setPosts(fallbackPosts);
      } finally {
        setLoading(false);
      }
    };

    fetchInstagramPosts();
  }, [backendUrl, fallbackPosts]);

  return (
    <section className="py-24 bg-secondary/30" data-testid="instagram-hub">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Instagram className="w-8 h-8 text-[hsl(var(--accent))]" />
            <h2 className="text-4xl sm:text-5xl font-bold">
              Instagram & BTS
            </h2>
          </div>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-6">
            Folge mir auf Instagram für Behind-the-Scenes, aktuelle Projekte und mehr
          </p>
          <a
            href="https://instagram.com/kennyfrey_"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:opacity-90 transition-opacity font-semibold"
          >
            <Instagram className="w-5 h-5" />
            @kennyfrey_ folgen
          </a>
          
          {!hasToken && !loading && (
            <p className="text-xs text-muted-foreground mt-4">
              💡 Live Feed wird geladen, sobald Instagram API konfiguriert ist
            </p>
          )}
        </div>

        {/* Instagram Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="aspect-square bg-secondary/50 rounded-xl animate-pulse"></div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {posts.slice(0, 8).map((post) => (
              <a
                key={post.id}
                href={post.permalink || post.link || "https://instagram.com/kennyfrey_"}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative aspect-square bg-secondary rounded-xl overflow-hidden hover-tilt cursor-pointer"
              >
                <img
                  src={post.media_url || post.thumbnail_url || post.image}
                  alt={post.caption || post.alt || "Instagram Post"}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  loading="lazy"
                />
                
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                  {(post.caption || post.alt) && (
                    <p className="text-white text-sm line-clamp-2">{post.caption || post.alt}</p>
                  )}
                  <ExternalLink className="absolute top-4 right-4 w-5 h-5 text-white" />
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default InstagramHub;
